import { wrapRequest } from '@applandeo/api-wrapper';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { ALERT_VARIANT, useAlertMessages } from '@/components/Global/AlertMessages/AlertMessagesProvider';
import { acceptRequestDeletion, rejectRequestDeletion } from '@/dataExchange/services/requestsPage.service';
import { NewRequestWhere } from '@/domain/newRequest';
import useTrackAbsenceApproval from '@/hooks/useTrackAbsenceApproval/useTrackAbsenceApproval';
import { MIXPANEL_ACTIONS, mixpanelTracking } from '@/mixPanel/mixPanelActions';
import { RequestDetailsAcceptanceContext } from '@/pages/Calendar/context/requestDetailsAcceptanceContext';
import { REQUEST_STATUS } from '@/utils/enums/requestsPage';

const useAcceptDeletionLogic = ({
    absenceId,
    where,
    absenceName,
    onCloseModal,
    getRequests,
}: {
    absenceId: string;
    where?: NewRequestWhere;
    absenceName?: string;
    onCloseModal?: () => void;
    getRequests: () => void;
}) => {
    const { triggerCalendarUpdate } = useContext(RequestDetailsAcceptanceContext);
    const { trackApproval } = useTrackAbsenceApproval();

    const AcceptRequestDeletionWrapper = wrapRequest(acceptRequestDeletion);
    const RejectRequestDeletionWrapper = wrapRequest(rejectRequestDeletion);

    const { t } = useTranslation();
    const { createAlert } = useAlertMessages();

    const acceptDelete = useCallback(() => {
        AcceptRequestDeletionWrapper.fetch(absenceId)
            .onSuccess(() => {
                createAlert(ALERT_VARIANT.success, t('requests_alerts_deletion_request_success'));
                getRequests();
                onCloseModal?.();
                trackApproval({ isDeletionRequest: true, absenceName, status: REQUEST_STATUS.ACCEPTED, where });
                triggerCalendarUpdate?.();
            })
            .onLocalError(() => {
                createAlert(ALERT_VARIANT.error, t('requests_alerts_deletion_request_failed'));
            });
    }, [absenceId]);

    const rejectDelete = useCallback(() => {
        RejectRequestDeletionWrapper.fetch(absenceId)
            .onSuccess(() => {
                createAlert(ALERT_VARIANT.success, t('requests_alerts_deletion_reject_success'));
                getRequests();
                mixpanelTracking(MIXPANEL_ACTIONS.REJECTED_DELETION_REQUEST, { absenceName, where });
                onCloseModal?.();
            })
            .onLocalError(() => {
                createAlert(ALERT_VARIANT.error, t('requests_alerts_deletion_reject_failed'));
            });
    }, [absenceId]);

    return {
        acceptDelete,
        rejectDelete,
        isActionLoading: RejectRequestDeletionWrapper.isLoading || RejectRequestDeletionWrapper.isLoading,
    };
};

export default useAcceptDeletionLogic;
